import { CircleCloseIcon, UploadIcon } from 'assets/icons';
import { CustomButton, CustomModal, CustomTextArea, MultiFileUpload } from 'components';
import { FILE_TYPE, PRESIGNED_UPLOAD_USE_CASE } from 'constants/common';
import { FC, useEffect, useState } from 'react';
import { AttachmentFile, UploadAttachmentFile, VoidFn } from 'types/baseTypes';
import { useCreateEstimateNoteMutation, useEditEstimateNoteMutation } from '../api';
import { ButtonType } from 'components/custom-button/CustomButton';
import { notesMaxCount } from 'containers/settings/constants';

export interface EstimateNote {
  noteId: number;
  title: string;
  attachments: AttachmentFile[];
  subTitle: string;
}

interface CreateEditEstimateNoteModalProps {
  defaultNote?: string;
  onClose: VoidFn;
  isEdit: boolean;
  currentNote: EstimateNote;
  teamId: number;
  estimateId: string;
  getEstimateTimeline: VoidFn;
  setNotesInput: (note: string) => void;
}

const CreateEditEstimateNoteModal: FC<CreateEditEstimateNoteModalProps> = ({
  defaultNote = '',
  onClose,
  isEdit,
  currentNote,
  teamId,
  estimateId,
  getEstimateTimeline,
  setNotesInput
}) => {
  const [comment, setComment] = useState(defaultNote);
  const [noteAttachments, setNoteAttachments] = useState<UploadAttachmentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadAbortController] = useState(() => new AbortController());
  const [createNoteMutation, createNoteState] = useCreateEstimateNoteMutation();
  const { isLoading: isCreateNoteLoading, isSuccess: isCreateNoteSuccess } = createNoteState;
  const [editNoteMutation, editNoteState] = useEditEstimateNoteMutation();
  const { isLoading: isEditNoteLoading, isSuccess: isEditNoteSuccess } = editNoteState;

  useEffect(() => {
    if (isEdit && currentNote) {
      setComment(currentNote.subTitle);
      setNoteAttachments(currentNote.attachments ?? []);
    }
  }, [currentNote, isEdit]);

  useEffect(() => {
    if ((!isCreateNoteLoading && isCreateNoteSuccess) || (!isEditNoteLoading && isEditNoteSuccess)) {
      getEstimateTimeline();
      setNotesInput('');
      onClose();
    }
  }, [isCreateNoteLoading, isCreateNoteSuccess, isEditNoteLoading, isEditNoteSuccess]);

  const formatAttachments = (): AttachmentFile[] => {
    return noteAttachments.map(file => ({
      id: file.id,
      title: file.title,
      url: file.url,
      description: file.description,
      urlLink: file.urlLink,
      source: 'ESTIMATE'
    })) as AttachmentFile[];
  };

  const onSaveClick = () => {
    if (isEdit) {
      editNoteMutation({
        comment: comment.trim(),
        attachments: formatAttachments(),
        noteId: currentNote.noteId,
        teamId,
        estimateId
      });
    } else {
      createNoteMutation({ comment: comment.trim(), attachments: formatAttachments(), estimateId, teamId });
    }
  };

  const onFileRemove = (id: number | string) => {
    const updatedAttachments = noteAttachments.filter(attachment => attachment.id !== id && attachment.tempId !== id);
    setNoteAttachments(updatedAttachments);
  };

  const handleClose = () => {
    uploadAbortController.abort();
    onClose();
  };

  const isSaveDisabled = () => {
    if (isUploading) return true;
    if (isEdit) {
      if (comment.trim() !== currentNote.subTitle.trim()) return false;
      if (noteAttachments?.length !== currentNote.attachments?.length) return false;
      for (let i = 0; i < noteAttachments?.length; i++) {
        if (noteAttachments[i].url !== currentNote.attachments[i].url) return false;
      }
    } else if (comment || noteAttachments?.length > 0) return false;
    return true;
  };

  return (
    <CustomModal open={true} width="md:w-[610px]" height="md:min-h-[440px] md:max-h-[678px]">
      <div className="relative flex grow flex-col">
        <CircleCloseIcon onClick={handleClose} className="absolute right-2 top-2 shrink-0" />

        <div className="flex items-center justify-between  px-10 pt-9">
          <div className="text-xl font-bold text-headingGray">{isEdit ? 'Edit note' : 'Create note'}</div>
        </div>
        <div className="mt-4 w-full border-b border-borderGray" />
        <div className="customNormalScroll max-h-[520px] overflow-auto px-10 pt-4 pb-2.5">
          <div className="mb-2 text-sm font-semibold text-primaryText">Note</div>
          <CustomTextArea
            id={isEdit ? 'edit-estimate-note-text-input' : 'create-estimate-note-text-input'}
            name="note-input"
            placeholder="Enter note here"
            className="h-[85px] py-3 px-5 focus:outline-none"
            value={comment}
            onChange={setComment}
            maxLength={notesMaxCount}
            showRemainingChar={true}
          />
          <div className="mt-4 mb-2 text-sbase font-semibold text-primaryText">Attachments</div>
          <MultiFileUpload
            id={isEdit ? 'create-estimate-note-add-attachment-card' : 'edit-estimate-note-add-attachment-card'}
            setFiles={setNoteAttachments}
            files={noteAttachments}
            onFileRemove={onFileRemove}
            className="mb-5"
            height="h-[100px]"
            FileUploadIcon={UploadIcon}
            supportedFileTypes={{
              [FILE_TYPE.JPG]: [],
              [FILE_TYPE.JPEG]: [],
              [FILE_TYPE.PNG]: [],
              [FILE_TYPE.WEBP]: []
            }}
            teamId={teamId.toString()}
            useCase={PRESIGNED_UPLOAD_USE_CASE.ESTIMATES}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadToPresigned={true}
            uploadAbortSignal={uploadAbortController.signal}
            previewClassName="max-h-[188px]"
          />
        </div>
        <div className="flex items-center justify-end border-t border-borderGray py-4 pr-10">
          <CustomButton
            id="edit-estimate-schedule-cancel-button"
            type={ButtonType.SECONDARY}
            onClick={handleClose}
            className="mr-5 w-20">
            Cancel
          </CustomButton>
          <CustomButton
            id="edit-estimate-schedule-save-button"
            type={ButtonType.PRIMARY}
            onClick={onSaveClick}
            disabled={isSaveDisabled()}
            className="w-20"
            isLoading={isCreateNoteLoading || isEditNoteLoading}>
            {isEdit ? 'Save' : 'Add'}
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default CreateEditEstimateNoteModal;
